// src/components/admin/ManageAnnouncements.jsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Megaphone,
  Archive,
  AlertTriangle,
  Plus,
  Search,
  Pencil,
  Trash2,
  Eye,
  X,
  Save,
  Calendar,
  Clock,
  AlertCircle,
  CheckCircle,
  Globe
} from 'lucide-react';
import RichTextEditor from '../common/RichTextEditor';
import uploadEditorImage from '../../utils/uploadEditorImage';
import axios from '../../utils/axios';
import { useAuth } from '../../context/AuthContext';
import Unauthorized from './UnauthorizedAccess';
import { EIC_ADMIN_ROLES } from './utils/AllowedRoles';
import { showToast } from '../../utils/toast';

// Priority Badge Component
const PriorityBadge = ({ priority }) => {
  const getPriorityClass = () => {
    switch(priority) {
      case 1:
        return 'bg-red-100 text-red-800 border-red-200';
      case 0:
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case -1:
        return 'bg-green-100 text-green-800 border-green-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getPriorityLabel = () => {
    switch(priority) {
      case 1:
        return 'High Priority';
      case 0:
        return 'Medium Priority';
      case -1:
        return 'Low Priority';
      default:
        return priority || 'Unknown';
    }
  };

  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${getPriorityClass()}`}>
      {getPriorityLabel()}
    </span>
  );
};


// View Announcement Modal Component
const ViewAnnouncementModal = ({ isOpen, onClose, announcement }) => {
  if (!isOpen || !announcement) return null;

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hour12: true
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[--asfi-purple] bg-opacity-10 rounded-lg flex items-center justify-center">
                <Megaphone className="w-5 h-5 text-[--asfi-purple]" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Announcement Details</h3>
                <p className="text-sm text-gray-500">View full announcement information</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="space-y-6">
            {/* Title and Priority */}
            <div className="flex items-start justify-between">
              <h2 className="text-2xl font-bold text-gray-900 pr-4">{announcement.title}</h2>
              <PriorityBadge priority={announcement.priority} />
            </div>

            {/* Meta Information */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 bg-gray-50 p-4 rounded-lg">
              <div>
                <p className="text-xs text-gray-500 mb-1">Author</p>
                <p className="text-sm font-medium text-gray-900">{announcement.authorEmail?.split('@')[0] || 'Unknown'}</p>
                <p className="text-xs text-gray-500">{announcement.authorEmail}</p>
              </div>
              
              <div>
                <p className="text-xs text-gray-500 mb-1">Date Created</p>
                <p className="text-sm text-gray-900">{formatDate(announcement.dateCreated)}</p>
              </div>
              
              <div>
                <p className="text-xs text-gray-500 mb-1">Status</p>
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                  announcement.status === 'active' 
                    ? 'bg-green-100 text-green-800'
                    : announcement.status === 'archived'
                    ? 'bg-gray-100 text-gray-800'
                    : 'bg-yellow-100 text-yellow-800'
                }`}>
                  {announcement.status?.charAt(0).toUpperCase() + announcement.status?.slice(1)}
                </span>
              </div>
            </div>

            {/* Dates */}
            <div className="flex gap-4 text-sm text-gray-600">
              {announcement.startDate && (
                <span className="flex items-center">
                  <Calendar className="w-4 h-4 mr-1 text-gray-400" />
                  Starts: {formatDate(announcement.startDate)}
                </span>
              )}
              {announcement.endDate && (
                <span className="flex items-center">
                  <Clock className="w-4 h-4 mr-1 text-gray-400" />
                  Ends: {formatDate(announcement.endDate)}
                </span>
              )}
            </div>

            {/* Content - Fixed HTML rendering */}
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Content</h4>
              <div className="bg-gray-50 p-4 rounded-lg">
                <div 
                  className="prose prose-sm max-w-none"
                  style={{ 
                    listStyleType: 'disc',
                    listStylePosition: 'inside'
                  }}
                >
                  <div 
                    dangerouslySetInnerHTML={{ 
                      __html: announcement.content 
                    }} 
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="mt-8 flex justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-[--asfi-purple] text-white rounded-lg hover:bg-[--asfi-blue] transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Announcement Modal Component (Create/Edit)
const AnnouncementModal = ({ isOpen, onClose, onSubmit, announcement, mode = 'create' }) => {
  const { user } = useAuth();
  
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    priority: 0, // Default to Medium (0)
  });

  const [formErrors, setFormErrors] = useState({});
  const [saveLoading, setSaveLoading] = useState(false);
  const [contentWordCount, setContentWordCount] = useState(0);

  // Load announcement data when editing
  useEffect(() => {
    if (announcement && mode === 'edit') {
      setFormData({
        title: announcement.title || '',
        content: announcement.content || '',
        priority: announcement.priority !== undefined ? announcement.priority : 0,
      });
    } else {
      // Reset form for new announcement
      setFormData({
        title: '',
        content: '',
        priority: 0,
      });
    }
    setContentWordCount(0);
    setFormErrors({});
  }, [announcement, mode, isOpen]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: name === 'priority' ? parseInt(value, 10) : value }));
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleContentChange = useCallback((content) => {
    setFormData(prev => ({ ...prev, content }));
    if (formErrors.content) {
      setFormErrors(prev => ({ ...prev, content: '' }));
    }
  }, [formErrors.content]);

  const handleWordCountChange = useCallback((count) => {
    setContentWordCount(count);
  }, []);

  const validateForm = () => {
    const errors = {};
    
    if (!formData.title.trim()) {
      errors.title = 'Title is required';
    }
    
    if (!formData.content.trim()) {
      errors.content = 'Content is required';
    } else if (contentWordCount < 10) {
      errors.content = 'Content must be at least 10 words';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setSaveLoading(true);
    
    try {
      // Prepare data for API
      const apiData = {
        title: formData.title,
        content: formData.content,
        priority: formData.priority
      };
      
      // Add id for edit mode
      if (mode === 'edit' && announcement?.id) {
        apiData.id = announcement.id;
      }
      
      await onSubmit(apiData);
      onClose();
    } catch (error) {
      console.error('Error saving announcement:', error);
      setFormErrors(prev => ({
        ...prev,
        submit: error.message || 'Failed to save announcement'
      }));
    } finally {
      setSaveLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <Megaphone className="w-5 h-5 mr-2 text-[--asfi-purple]" />
                {mode === 'create' ? 'Create New Announcement' : 'Edit Announcement'}
              </h3>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Error Message */}
          {formErrors.submit && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center text-red-700">
              <AlertCircle className="w-4 h-4 mr-2 flex-shrink-0" />
              <span className="text-sm">{formErrors.submit}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Title */}
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                Announcement Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[--asfi-purple] focus:border-transparent ${
                  formErrors.title ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Enter announcement title..."
              />
              {formErrors.title && (
                <p className="mt-1 text-xs text-red-600">{formErrors.title}</p>
              )}
            </div>

            {/* Content with RichTextEditor */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Announcement Content <span className="text-red-500">*</span>
              </label>
              <RichTextEditor
                value={formData.content}
                onChange={handleContentChange}
                onWordCountChange={handleWordCountChange}
                onImageExtracted={uploadEditorImage}
                placeholder="Enter your announcement content here..."
                minHeight="200px"
                maxHeight="300px"
                showWordCount={true}
                wordLimit={1000}
                toolbarOptions="full"
              />
              {formErrors.content && (
                <p className="mt-1 text-xs text-red-600">{formErrors.content}</p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                Minimum 10 words. Current: {contentWordCount} words
              </p>
            </div>

            {/* Priority */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="priority" className="block text-sm font-medium text-gray-700 mb-1">
                  Priority Level
                </label>
                <select
                  id="priority"
                  name="priority"
                  value={formData.priority}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[--asfi-purple] focus:border-transparent"
                >
                  <option value={-1}>Low</option>
                  <option value={0}>Medium</option>
                  <option value={1}>High</option>
                </select>
              </div>
            </div>

            {/* Author Information - Display Only */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Author Email
                </label>
                <input
                  type="email"
                  value={user?.email || 'Not available'}
                  disabled
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-700 font-medium"
                />
                <p className="mt-1 text-xs text-gray-400">Auto-filled from your account</p>
              </div>
            </div>

            {/* Form Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saveLoading}
                className="px-4 py-2 bg-[--asfi-purple] text-white rounded-lg hover:bg-[--asfi-blue] transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saveLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    {mode === 'create' ? 'Create Announcement' : 'Save Changes'}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// Main Component
const ManageAnnouncements = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  // Check if user has required editorial level
    const isAuthorized = user && EIC_ADMIN_ROLES.includes(user.editorial_level || user.editorialLevel);
  
    if (!isAuthorized) {
      return (
        <Unauthorized
          message="You need editor-in-chief or admin privileges to access this feature."
        />
      );
  }
  // State
  const [announcements, setAnnouncements] = useState([]);
  const [filteredAnnouncements, setFilteredAnnouncements] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    archived: 0,
    highPriority: 0
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Fetch announcements from API
  const fetchAnnouncements = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/editors/getAnnouncements');
      
      if (response.data.status === 'success') {
        setAnnouncements(response.data.announcements);
        setFilteredAnnouncements(response.data.announcements);
        
        // Calculate stats from fetched data
        
        const active = response.data.announcements.filter(a => a.status === 'active').length;
        const archived = response.data.announcements.filter(a => a.status === 'archived').length;
        const highPriority = response.data.announcements.filter(a => a.priority === 1 && a.status === 'active').length;
        
        setStats({
          total: response.data.announcements.length,
          active,
          archived,
          highPriority
        });
      } else {
        showToast('Failed to fetch announcements', 'error');
      }
    } catch (error) {
      console.error('Error fetching announcements:', error);
      showToast(error.response?.data?.message || 'Failed to fetch announcements', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Load announcements on mount
  useEffect(() => {
    fetchAnnouncements();
  }, []);

  // Filter announcements based on search
  useEffect(() => {
    const filtered = announcements.filter(announcement => 
      announcement.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      announcement.content?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      announcement.authorEmail?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredAnnouncements(filtered);
    setCurrentPage(1);
  }, [searchTerm, announcements]);

  // Pagination
  const totalPages = Math.ceil(filteredAnnouncements.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentAnnouncements = filteredAnnouncements.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleCreateClick = () => {
    setModalMode('create');
    setSelectedAnnouncement(null);
    setShowModal(true);
  };

  const handleEditClick = (announcement) => {
    setModalMode('edit');
    setSelectedAnnouncement(announcement);
    setShowModal(true);
  };

  const handleViewClick = (announcement) => {
    setSelectedAnnouncement(announcement);
    setShowViewModal(true);
  };

  const handleDeleteClick = (announcement) => {
    setDeleteConfirm(announcement);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm) return;

    try {
      const response = await axios.post('/editors/deleteAnnouncement', {
        id: deleteConfirm.id
      });

      if (response.data.status === 'success') {
        await fetchAnnouncements(); // Refresh the list to update stats
        showToast('Announcement deleted successfully', 'success');
      } else {
        throw new Error(response.data.message || 'Failed to delete announcement');
      }
    } catch (error) {
      console.error('Error deleting announcement:', error);
      showToast(error.response?.data?.message || 'Failed to delete announcement', 'error');
    } finally {
      setDeleteConfirm(null);
    }
  };

  const handleSubmitAnnouncement = async (announcementData) => {
    try {
      let response;
      
      if (modalMode === 'create') {
        response = await axios.post('/editors/uploadAnnouncement', announcementData);
      } else {
        response = await axios.post('/editors/editAnnouncement', announcementData);
      }

      if (response.data.status === 'success') {
        // Refresh announcements list to get updated data
        await fetchAnnouncements();
        
        showToast(modalMode === 'create' 
          ? 'Announcement created successfully' 
          : 'Announcement updated successfully', 'success');
      } else {
        throw new Error(response.data.message || 'Failed to save announcement');
      }
    } catch (error) {
      console.error('Error saving announcement:', error);
      throw new Error(error.response?.data?.message || 'Failed to save announcement');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
      hour12: true
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0 w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Delete Announcement
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">
                    This action cannot be undone.
                  </p>
                </div>
              </div>

              <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                <p className="text-sm font-medium text-gray-700">Title:</p>
                <p className="text-sm text-gray-900 mt-1">{deleteConfirm.title}</p>
                <p className="text-xs text-gray-500 mt-1">ID: {deleteConfirm.id}</p>
              </div>

              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-800 flex items-start">
                  <AlertTriangle className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
                  <span>
                    <strong>Warning:</strong> This will permanently delete this announcement.
                  </span>
                </p>
              </div>

              <div className="mt-6 flex justify-end gap-3">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteConfirm}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete Permanently
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="px-6 py-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Total Announcements */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 mb-1">Total Announcements</p>
                <p className="text-3xl font-bold text-gray-900">{stats.total}</p>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <Megaphone className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </div>

          {/* Active Announcements */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 mb-1">Active Announcements</p>
                <p className="text-3xl font-bold text-gray-900">{stats.active}</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </div>

          {/* Archived */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 mb-1">Archived</p>
                <p className="text-3xl font-bold text-gray-900">{stats.archived}</p>
              </div>
              <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                <Archive className="w-6 h-6 text-gray-600" />
              </div>
            </div>
          </div>

          {/* High Priority */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 mb-1">High Priority</p>
                <p className="text-3xl font-bold text-gray-900">{stats.highPriority}</p>
              </div>
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </div>

          {/* Create New Button */}
          <button
            onClick={handleCreateClick}
            className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-all hover:border-[--asfi-purple] group col-span-1"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 mb-1 group-hover:text-[--asfi-purple]">Create New</p>
                <p className="text-xl font-semibold text-gray-900 group-hover:text-[--asfi-purple]">Announcement</p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center group-hover:bg-[--asfi-purple] group-hover:text-white transition-colors">
                <Plus className="w-6 h-6 text-green-600 group-hover:text-white" />
              </div>
            </div>
          </button>
        </div>
      </div>

      {/* Announcements Table */}
      <div className="px-6 pb-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          {/* Table Header with Search */}
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Current Announcements</h3>
              <div className="relative w-64">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search announcements..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[--asfi-purple] focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Priority</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Author</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date Created</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-8 text-center">
                      <div className="flex justify-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[--asfi-purple]"></div>
                      </div>
                    </td>
                  </tr>
                ) : currentAnnouncements.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                      No announcements found
                    </td>
                  </tr>
                ) : (
                  currentAnnouncements.map((announcement) => (
                    <tr key={announcement.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">{announcement.title}</div>
                      </td>
                      <td className="px-6 py-4">
                        <PriorityBadge priority={announcement.priority} />
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-600">{announcement.authorEmail}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-600">{formatDate(announcement.dateCreated)}</div>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => handleEditClick(announcement)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Edit"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteClick(announcement)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleViewClick(announcement)}
                            className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                            title="View"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-6 py-4 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-600">
                  Showing {startIndex + 1} to {Math.min(endIndex, filteredAnnouncements.length)} of {filteredAnnouncements.length} results
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    Previous
                  </button>
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`w-8 h-8 rounded text-sm ${
                          currentPage === pageNum
                            ? 'bg-[--asfi-purple] text-white'
                            : 'border border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Announcement Modal (Create/Edit) */}
      <AnnouncementModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onSubmit={handleSubmitAnnouncement}
        announcement={selectedAnnouncement}
        mode={modalMode}
      />

      {/* View Announcement Modal */}
      <ViewAnnouncementModal
        isOpen={showViewModal}
        onClose={() => setShowViewModal(false)}
        announcement={selectedAnnouncement}
      />

      {/* Footer */}
      <footer className="text-center py-4 border-t border-gray-200 bg-white">
        <p className="text-sm text-gray-500">
          {new Date().getFullYear()} © ASFI - all rights reserved
        </p>
      </footer>
    </div>
  );
};

export default ManageAnnouncements;